/**
 * Register component for user registration
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';
import { Loader2, User, Lock, GraduationCap, Building } from 'lucide-react';

import { registerWithEmailAndPassword } from '../../../services/firebase/authService';
import { initializeUserProfile } from '../../../services/firebase/profileService';
import { sendOTP, verifyOTP } from '../../../services/firebase/authService';
import { universities } from '../../../core/constants/universities';
import useFormValidation from '../hooks/useFormValidation';

import EmailInput from './EmailInput';
import OTPVerification from './OTPVerification';
import PrivacyPolicyModal from './PrivacyPolicyModal';

/**
 * Register component
 * 
 * @param {Object} props - Component props
 * @param {Function} props.switchToLogin - Function to switch to login view
 * @returns {JSX.Element} Register component
 */
const Register = ({ switchToLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  
  // Form validation function
  const validateForm = (values) => {
    const errors = {};
    
    // Name validation
    if (!values.name.trim()) {
      errors.name = 'Full name is required';
    }

    // Username validation
    if (!values.username) {
      errors.username = 'Username is required';
    } else if (values.username.length < 3) {
      errors.username = 'Username must be at least 3 characters long';
    } else if (values.username.length > 20) {
      errors.username = 'Username must be less than 20 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(values.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // University validation
    if (!values.university) {
      errors.university = 'Please select your university';
    }

    // Email validation
    if (!values.email) {
      errors.email = 'University email is required';
    } else {
      const selectedUniversity = universities.find(uni => uni.name === values.university);
      if (selectedUniversity) {
        const emailDomain = values.email.substring(values.email.indexOf('@'));
        if (emailDomain !== selectedUniversity.domain) {
          errors.email = `Please use your ${selectedUniversity.name} email address (${selectedUniversity.domain})`;
        }
      }
    }

    // Password validation
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  // Initialize form with validation
  const {
    values: formData,
    errors,
    isSubmitting,
    isValid,
    checkingUsername,
    handleChange,
    handleSubmit,
    setError
  } = useFormValidation(
    {
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      university: ''
    },
    validateForm
  );

  // Send OTP to user email
  const handleSendOTP = async (email) => {
    setLoading(true);
    try {
      await sendOTP(email);
      setVerificationEmail(email);
      setShowOTP(true);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setError('backend', 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP submitted by user
  const handleVerifyOTP = async (otpCode) => {
    try {
      await verifyOTP(verificationEmail, otpCode);
      await completeRegistration();
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw new Error(error.message || 'Invalid verification code');
    }
  };

  // Complete registration process after OTP verification
  const completeRegistration = async () => {
    setLoading(true);
    try {
      // Create user with Firebase auth
      const userCredential = await registerWithEmailAndPassword(
        formData.email,
        formData.password
      );

      // Update user profile with display name
      await updateProfile(userCredential.user, {
        displayName: formData.name,
        photoURL: `./avi.png`
      });

      // Initialize user profile in Firestore
      await initializeUserProfile(userCredential.user.uid, formData);

      // Get ID token and store in local storage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);

      // Navigate to home page
      navigate('/home');
    } catch (error) {
      console.error('Error during registration:', error);
      throw new Error(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Get error message for Firebase auth errors
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled. Please contact support';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters';
      default:
        return 'Registration failed. Please try again later';
    }
  };
  
  // Handle form submission
  const onSubmit = (e) => {
    e.preventDefault();
    if (handleSubmit(e)) {
      setShowPrivacyPolicy(true);
    }
  };
  
  // Handle privacy policy acceptance
  const handlePrivacyPolicyAccept = async () => {
    setShowPrivacyPolicy(false);
    try {
      await handleSendOTP(formData.email);
    } catch (error) {
      setError('backend', error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8 mt-6 sm:mt-0">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 mt-8 sm:mt-0">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                <span className="text-white font-bold text-2xl transform -rotate-12">V</span>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="text-green-500">Varsity</span>Link
          </h1>
          <div className="flex items-center justify-center space-x-2 mt-2">
            <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full flex items-center">
              <GraduationCap className="w-3 h-3 mr-1" />
              The Ultimate Student Connection Hub
            </span>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-6">
          <h2 className="text-2xl font-semibold text-gray-800 text-center">Create Account</h2>
          
          {!showOTP ? (
            <form onSubmit={onSubmit} className="space-y-4">
              {/* Full Name Input */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.name ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name}</p>
                )}
              </div>

              {/* Username Input */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`pl-10 pr-10 py-3 w-full bg-gray-50 border ${
                      errors.username ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {checkingUsername ? (
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    ) : (
                      <span className="text-gray-400">@</span>
                    )}
                  </div>
                </div>
                {errors.username && (
                  <p className="text-red-500 text-sm">{errors.username}</p>
                )}
              </div>

              {/* University Selection */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="university"
                    value={formData.university}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.university ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  >
                    <option value="">Select University</option>
                    {universities.map((uni) => (
                      <option key={uni.name} value={uni.name}>
                        {uni.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.university && (
                  <p className="text-red-500 text-sm">{errors.university}</p>
                )}
              </div>

              {/* Email Input */}
              <EmailInput 
                formData={formData}
                handleChange={handleChange}
                errors={errors}
                universities={universities}
              />

              {/* Password Input */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.password ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !isValid}
                className={`w-full font-medium py-3 rounded-xl flex items-center justify-center space-x-2 transition-colors ${
                  loading || !isValid
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating Account...</span>
                  </>
                ) : (
                  <span>Create Account</span>
                )}
              </button>
            </form>
          ) : (
            <OTPVerification
              email={verificationEmail}
              onVerified={handleVerifyOTP}
              resendOTP={() => handleSendOTP(verificationEmail)}
            />
          )}

          <PrivacyPolicyModal 
            isOpen={showPrivacyPolicy}
            onClose={() => setShowPrivacyPolicy(false)}
            onAccept={handlePrivacyPolicyAccept}
          />

          {errors.backend && (
            <div className="bg-red-50 text-red-500 p-3 rounded-xl text-sm">
              {errors.backend}
            </div>
          )}

          {/* Switch to Login */}
          <div className="text-center text-sm">
            <span className="text-gray-600">Already have an account?</span>{' '}
            <button
              onClick={switchToLogin}
              className="text-green-500 hover:text-green-600 font-medium"
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;